import type { PassageUserType } from "@/src/shared/contracts/app";
import { hasVerifiedCheckoutSubscription } from "@/src/features/billing/plans";

export const VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE = "VERIFIED_CHECKOUT_SESSION_SYNC";

export type VerifiedCheckoutSyncMessage = {
  type: typeof VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE;
  sessionId: string;
  userId: string;
  companyId: string;
  timestamp: number;
};

type CheckoutVerificationResponse = {
  status?: unknown;
  payments?: PassageUserType["payments"];
};

const CHECKOUT_SESSION_ID_PATTERN = /^cs_(?:(?:test|live)_)?[A-Za-z0-9]{1,240}$/;

export function createVerifiedCheckoutSyncMessage(
  userSession: PassageUserType,
  sessionId: string,
): VerifiedCheckoutSyncMessage | null {
  const userId = String(userSession.userId || "").trim();
  const companyId = String(userSession.companyId || "").trim();
  if (
    userSession.isAuthorized !== true
    || !userId
    || !companyId
    || !CHECKOUT_SESSION_ID_PATTERN.test(sessionId)
  ) return null;

  return {
    type: VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE,
    sessionId,
    userId,
    companyId,
    timestamp: Date.now(),
  };
}

export function isVerifiedCheckoutSyncForSession(
  value: unknown,
  userSession: PassageUserType,
): value is VerifiedCheckoutSyncMessage {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const message = value as Partial<VerifiedCheckoutSyncMessage>;
  return message.type === VERIFIED_CHECKOUT_SYNC_MESSAGE_TYPE
    && CHECKOUT_SESSION_ID_PATTERN.test(String(message.sessionId || ""))
    && userSession.isAuthorized === true
    && message.userId === String(userSession.userId || "").trim()
    && message.companyId === String(userSession.companyId || "").trim();
}

/**
 * A broadcast is only a prompt to reverify. Unlock data must come from the
 * authenticated checkout confirmation route in each receiving renderer.
 */
export function mergeReverifiedCheckoutSession(
  userSession: PassageUserType,
  response: CheckoutVerificationResponse,
): PassageUserType | null {
  if (response.status !== "success" || !hasVerifiedCheckoutSubscription(response.payments)) {
    return null;
  }
  const verifiedPayments = response.payments!;
  const customerId = String(
    verifiedPayments.customerId || verifiedPayments.stripeCustomerId || "",
  ).trim();

  return {
    ...userSession,
    payments: {
      ...(userSession.payments || {}),
      ...verifiedPayments,
      subscriptionStatus: String(verifiedPayments.subscriptionStatus),
      subscriptionId: String(verifiedPayments.subscriptionId),
      customerId,
    },
  };
}
